import {
  MenuItem,
  MenuTrigger,
  RadioGroup,
  type RadioItem,
} from '@affine/component';
import { SettingRow } from '@affine/component/setting-components';
import { EditorSettingService } from '@affine/core/modules/editor-setting';
import { useI18n } from '@affine/i18n';
import { getSurfaceBlock } from '@blocksuite/affine/blocks/surface';
import { LayoutType, MindmapStyle } from '@blocksuite/affine/model';
import type { Store } from '@blocksuite/affine/store';
import { useFramework, useLiveData } from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

import { DropdownMenu } from '../menu';
import { menuTrigger, settingWrapper } from '../style.css';
import { EdgelessSnapshot } from './snapshot';

const MINDMAP_STYLES = [
  {
    value: MindmapStyle.ONE,
  },
  {
    value: MindmapStyle.TWO,
  },
  {
    value: MindmapStyle.THREE,
  },
  {
    value: MindmapStyle.FOUR,
  },
];

type I18n = ReturnType<typeof useI18n>;

const MINDMAP_STYLE_LABEL_KEYS = {
  [MindmapStyle.ONE]:
    'com.streamlain.settings.editorSettings.edgeless.mind-map.style.one',
  [MindmapStyle.TWO]:
    'com.streamlain.settings.editorSettings.edgeless.mind-map.style.two',
  [MindmapStyle.THREE]:
    'com.streamlain.settings.editorSettings.edgeless.mind-map.style.three',
  [MindmapStyle.FOUR]:
    'com.streamlain.settings.editorSettings.edgeless.mind-map.style.four',
} as const;

const getMindmapStyleLabel = (t: I18n, value: MindmapStyle) => {
  const key =
    MINDMAP_STYLE_LABEL_KEYS[value] ??
    MINDMAP_STYLE_LABEL_KEYS[MindmapStyle.FOUR];
  return t[key]();
};

export const MindMapSettings = () => {
  const t = useI18n();
  const framework = useFramework();
  const { editorSetting } = framework.get(EditorSettingService);
  const settings = useLiveData(editorSetting.settings$);

  const { layoutType } = settings.mindmap;
  const setLayoutType = useCallback(
    (value: LayoutType) => {
      editorSetting.set('mindmap', {
        layoutType: value,
      });
    },
    [editorSetting]
  );
  const layoutTypeItems = useMemo<RadioItem[]>(
    () => [
      {
        value: LayoutType.LEFT as any,
        label:
          t[
            'com.streamlain.settings.editorSettings.edgeless.mind-map.layout.left'
          ](),
      },
      {
        value: LayoutType.BALANCE as any,
        label:
          t[
            'com.streamlain.settings.editorSettings.edgeless.mind-map.layout.radial'
          ](),
      },
      {
        value: LayoutType.RIGHT as any,
        label:
          t[
            'com.streamlain.settings.editorSettings.edgeless.mind-map.layout.right'
          ](),
      },
    ],
    [t]
  );

  const styleItems = useMemo(() => {
    const { style } = settings.mindmap;
    return MINDMAP_STYLES.map(({ value }) => {
      const handler = () => {
        editorSetting.set('mindmap', { style: value });
      };
      const isSelected = style === value;
      const label = getMindmapStyleLabel(t, value);
      return (
        <MenuItem key={value} onSelect={handler} selected={isSelected}>
          {label}
        </MenuItem>
      );
    });
  }, [editorSetting, settings, t]);

  const getElements = useCallback((doc: Store) => {
    const surface = getSurfaceBlock(doc);
    return surface?.getElementsByType('mindmap') || [];
  }, []);

  return (
    <>
      <EdgelessSnapshot
        title={t['com.streamlain.settings.editorSettings.edgeless.mind-map']()}
        docName="mindmap"
        keyName={'mindmap' as any}
        getElements={getElements}
        height={320}
      />
      <SettingRow
        name={t['com.streamlain.settings.editorSettings.edgeless.style']()}
        desc={''}
      >
        <DropdownMenu
          items={styleItems}
          trigger={
            <MenuTrigger className={menuTrigger}>
              {getMindmapStyleLabel(t, settings.mindmap.style)}
            </MenuTrigger>
          }
        />
      </SettingRow>
      <SettingRow
        name={t[
          'com.streamlain.settings.editorSettings.edgeless.mind-map.layout'
        ]()}
        desc={''}
      >
        <RadioGroup
          items={layoutTypeItems}
          value={layoutType}
          width={250}
          className={settingWrapper}
          onChange={setLayoutType}
        />
      </SettingRow>
    </>
  );
};
